import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';

/**
 * Sacred Ritual Reveal — v2 (Feb 19, 2026 Luxury Redesign)
 *
 * Calm, luxurious, spiritual. No bounces. Only slow fades + ethereal glows.
 * Three stages over ~9 seconds:
 *   Stage 0 (0-3s):   Sanskrit shloka — gold gradient fade in, hold, fade out.
 *   Stage 1 (3-6.5s): Acharya soft portrait with pulsing halo + calm message.
 *   Stage 2 (6.5-9s): Gentle warm gold overlay + "आपका उत्तर तैयार है" reveal.
 */
export default function SacredRitualReveal({ userName = '', question = '', onComplete }) {
  const [stage, setStage] = useState(0);
  const reduce = useReducedMotion();
  const easeCalm = [0.16, 1, 0.3, 1];

  // Keep the latest onComplete in a ref so the useEffect below runs EXACTLY
  // once per mount. Without this, an inline `onComplete={() => ...}` prop from
  // the parent would be a new reference on every parent render → useEffect
  // would re-fire → the 9-sec timers would keep getting cleared and reset →
  // the ritual overlay would stay on screen forever, blocking the user on a
  // black full-viewport screen after payment (real user P0 bug, Feb 2026).
  const onCompleteRef = React.useRef(onComplete);
  React.useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  useEffect(() => {
    const dur = reduce ? 0.35 : 1;
    const t1 = setTimeout(() => setStage(1), 3000 * dur);
    const t2 = setTimeout(() => setStage(2), 6500 * dur);
    const t3 = setTimeout(() => onCompleteRef.current?.(), 9000 * dur);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduce]);

  return (
    <section
      className="fixed inset-0 z-40 flex items-center justify-center overflow-hidden"
      data-testid="sacred-ritual-reveal"
    >
      {/* Deep obsidian bg */}
      <div className="absolute inset-0 bg-[#050505]" />
      {/* Soft royal-purple aura center */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.6, ease: 'easeInOut' }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-[#3d101e]/45 blur-[200px] pointer-events-none"
      />
      {/* Gold breathing aura */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[440px] h-[440px] rounded-full bg-[#D4AF37]/8 blur-[130px] pointer-events-none"
        animate={{ opacity: [0.45, 0.75, 0.45], scale: [0.95, 1.05, 0.95] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Slow, extremely subtle yantra */}
      <motion.div
        className="absolute w-[75vmin] h-[75vmin] max-w-[620px] max-h-[620px] pointer-events-none opacity-[0.10]"
        animate={{ rotate: 360 }}
        transition={{ duration: 120, repeat: Infinity, ease: 'linear' }}
      >
        <svg viewBox="0 0 400 400" className="w-full h-full">
          {[196, 172, 144, 112, 78].map((r) => (
            <circle key={r} cx="200" cy="200" r={r} fill="none" stroke="#D4AF37" strokeOpacity="0.7" strokeWidth="0.5" />
          ))}
          {Array.from({ length: 12 }).map((_, i) => (
            <line key={i} x1="200" y1="200" x2="200" y2="12" stroke="#D4AF37" strokeOpacity="0.55" strokeWidth="0.35"
              transform={`rotate(${(i * 360) / 12} 200 200)`} />
          ))}
          <text x="200" y="220" textAnchor="middle" fontSize="52" fill="#D4AF37" fillOpacity="0.55" fontFamily="serif">
            ॐ
          </text>
        </svg>
      </motion.div>

      {/* Stage content */}
      <div className="container-custom relative z-10 max-w-2xl text-center">
        <AnimatePresence mode="wait">
          {stage === 0 && (
            <motion.div
              key="shloka"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.3, ease: 'easeInOut' }}
            >
              <p className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]/75 mb-10 font-light">
                पवित्र आह्वान
              </p>
              <p
                className="font-heading text-[26px] sm:text-4xl lg:text-[46px] text-gold-gradient italic leading-[1.75] tracking-tight font-light"
                data-testid="ritual-shloka"
              >
                ॐ ग्रहेभ्यो नमः।
                <br />
                ॐ नक्षत्रेभ्यो नमः॥
              </p>
              <p className="text-[#8A8A8A] italic text-sm sm:text-base max-w-md mx-auto leading-[1.9] mt-10 font-light">
                समस्त ग्रहों एवं नक्षत्रों को नमस्कार<br />
                कृपया आपके प्रश्न पर अपनी शुभ दृष्टि प्रदान करें।
              </p>
            </motion.div>
          )}

          {stage === 1 && (
            <motion.div
              key="greeting"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.3, ease: 'easeInOut' }}
              className="flex flex-col items-center"
            >
              {/* Portrait with pulsing halo */}
              <div className="relative mb-8">
                <motion.div
                  className="absolute inset-0 rounded-full bg-[#D4AF37]/15 blur-2xl"
                  animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.15, 1] }}
                  transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
                />
                <div className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden border border-[#D4AF37]/50 shadow-[0_0_50px_-6px_rgba(212,175,55,0.55)]">
                  <img
                    src="/brand/vivek-cutout.png"
                    alt="Acharya Vivek Mudgal"
                    className="w-full h-full object-cover object-top bg-gradient-to-b from-[#1a0710] to-[#050505]"
                  />
                </div>
              </div>

              <p className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]/75 mb-5 font-light">
                Acharya Vivek Ji
              </p>
              <h2
                className="font-heading text-[22px] sm:text-[28px] lg:text-3xl text-[#F5F5F5] leading-[1.5] font-light max-w-lg mx-auto mb-4 tracking-tight"
                data-testid="ritual-greeting"
              >
                {userName ? `${userName} जी, ` : ''}आपके प्रश्न पर पराशरीय दृष्टि लगाई जा रही है
              </h2>
              {question && (
                <p className="text-[#8A8A8A] italic text-sm sm:text-base max-w-md mx-auto font-light">
                  &ldquo;{question}&rdquo;
                </p>
              )}

              {/* Slow calming dots */}
              <div className="flex items-center gap-3 mt-10">
                {[0, 1, 2].map((i) => (
                  <motion.span
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"
                    animate={{ opacity: [0.2, 1, 0.2] }}
                    transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.35, ease: 'easeInOut' }}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {stage === 2 && (
            <motion.div
              key="ready"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2, ease: 'easeInOut' }}
            >
              {/* Warm gold wash */}
              <motion.div
                className="absolute inset-0 pointer-events-none mix-blend-screen bg-[#D4AF37]/8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.2 }}
              />
              {/* Simple gold gilded circle with tick */}
              <div className="relative inline-flex items-center justify-center mb-7">
                <div className="absolute w-24 h-24 rounded-full bg-[#D4AF37]/15 blur-2xl" />
                <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-[#F3E5AB] via-[#D4AF37] to-[#997A00] flex items-center justify-center shadow-[0_0_60px_-6px_rgba(212,175,55,0.7)]">
                  <span className="text-2xl text-[#0A0A0A] font-light">✓</span>
                </div>
              </div>
              <h2
                className="font-heading text-[30px] sm:text-4xl lg:text-[46px] text-gold-gradient leading-[1.15] tracking-tight font-light mb-4"
                data-testid="ritual-ready"
              >
                आपका उत्तर तैयार है
              </h2>
              <p className="text-[#A3A3A3] text-sm sm:text-base max-w-md mx-auto leading-[1.8] font-light">
                कृपया कुछ क्षण<br />शास्त्रीय विवेचना नीचे प्रकट हो रही है
              </p>
              <p className="mt-10 text-[10px] uppercase tracking-[0.4em] text-[#8A8A8A] font-light">
                🙏 धन्यवाद
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Subtle stage indicators */}
        <div className="absolute bottom-14 left-1/2 -translate-x-1/2 flex items-center gap-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`h-px transition-all duration-700 ${
                i === stage
                  ? 'w-8 bg-[#D4AF37]'
                  : i < stage
                    ? 'w-4 bg-[#D4AF37]/40'
                    : 'w-4 bg-white/10'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
